import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchRatePlans,
  createRatePlan,
  updateRatePlan,
  deleteRatePlan,
  fetchRoomTypes,
  createRoomType,
  updateRoomType,
  deleteRoomType,
  fetchRoomTypeCategories,
  createRoomTypeCategory,
  updateRoomTypeCategory,
  deleteRoomTypeCategory,
  fetchCancellationPolicies,
  createCancellationPolicy,
  updateCancellationPolicy,
  deleteCancellationPolicy,
  type RatePlansQuery,
  type CreateRatePlanPayload,
  type UpdateRatePlanPayload,
  type RoomTypesQuery,
  type CreateRoomTypePayload,
  type UpdateRoomTypePayload,
  type CreateRoomTypeCategoryPayload,
  type UpdateRoomTypeCategoryPayload,
  type CancellationPoliciesQuery,
  type CreateCancellationPolicyPayload,
  type UpdateCancellationPolicyPayload,
} from '@/services/api/revenue.api';

// ─── Rate Plans ──────────────────────────────────────────────────────────────

export function useRatePlans(query: RatePlansQuery, enabled = true) {
  return useQuery({
    queryKey: ['rate-plans', query],
    queryFn: () => fetchRatePlans(query),
    staleTime: 30_000,
    enabled,
  });
}

export function useCreateRatePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRatePlanPayload) => createRatePlan(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rate-plans'] });
    },
  });
}

export function useUpdateRatePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRatePlanPayload }) =>
      updateRatePlan(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rate-plans'] });
    },
  });
}

export function useDeleteRatePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRatePlan(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rate-plans'] });
    },
  });
}

// ─── Room Types ──────────────────────────────────────────────────────────────

export function useRoomTypes(query: RoomTypesQuery) {
  return useQuery({
    queryKey: ['room-types', query],
    queryFn: () => fetchRoomTypes(query),
    staleTime: 30_000,
  });
}

export function useCreateRoomType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRoomTypePayload) => createRoomType(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['room-types'] });
    },
  });
}

export function useUpdateRoomType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRoomTypePayload }) =>
      updateRoomType(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['room-types'] });
    },
  });
}

export function useDeleteRoomType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRoomType(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['room-types'] });
    },
  });
}

// ─── Room Type Categories ────────────────────────────────────────────────────

export function useRoomTypeCategories() {
  return useQuery({
    queryKey: ['room-type-categories'],
    queryFn: () => fetchRoomTypeCategories(),
    staleTime: 60_000,
  });
}

export function useCreateRoomTypeCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRoomTypeCategoryPayload) => createRoomTypeCategory(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['room-type-categories'] });
    },
  });
}

export function useUpdateRoomTypeCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRoomTypeCategoryPayload }) =>
      updateRoomTypeCategory(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['room-type-categories'] });
    },
  });
}

export function useDeleteRoomTypeCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteRoomTypeCategory(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['room-type-categories'] });
    },
  });
}

// ─── Cancellation Policies ───────────────────────────────────────────────────

export function useCancellationPolicies(query: CancellationPoliciesQuery) {
  return useQuery({
    queryKey: ['cancellation-policies', query],
    queryFn: () => fetchCancellationPolicies(query),
    staleTime: 30_000,
  });
}

export function useCreateCancellationPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCancellationPolicyPayload) => createCancellationPolicy(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cancellation-policies'] });
    },
  });
}

export function useUpdateCancellationPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCancellationPolicyPayload }) =>
      updateCancellationPolicy(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cancellation-policies'] });
    },
  });
}

export function useDeleteCancellationPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCancellationPolicy(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cancellation-policies'] });
    },
  });
}
